import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Alert, AlertDescription } from "../components/ui/alert";
import { 
  Package, 
  Search, 
  Filter,
  Eye,
  RefreshCw,
  ArrowLeft,
  Calendar,
  User,
  CreditCard,
  Truck,
  AlertCircle,
  CheckCircle,
  Trash2,
  LogOut,
  X
} from "lucide-react";

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingOrderId, setTrackingOrderId] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingCarrier, setTrackingCarrier] = useState("Australia Post");
  const ordersPerPage = 20;
  const navigate = useNavigate();

  const orderStatuses = [
    { value: "new_order", label: "New Order", color: "bg-yellow-100 text-yellow-800" },
    { value: "shipped", label: "Shipped", color: "bg-green-100 text-green-800" },
    { value: "shipped_no_notification", label: "Shipped (No Notification)", color: "bg-green-50 text-green-700 border border-green-200" }
  ];

  // Store managers log in via /manager and only get the orders view
  const isManager = localStorage.getItem("managerMode") === "true";

  const handleManagerLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminTokenExpires");
    localStorage.removeItem("managerMode");
    navigate("/manager");
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate(isManager ? "/manager" : "/admin/login");
      return null;
    }
    return {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    };
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const params = new URLSearchParams({
        limit: ordersPerPage.toString(),
        offset: ((currentPage - 1) * ordersPerPage).toString()
      });

      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders?${params}`, {
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const ordersData = await response.json();
      setOrders(ordersData);

      // Get total count for pagination
      const countParams = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') countParams.append('status', statusFilter);
      if (searchTerm) countParams.append('search', searchTerm);

      const countResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/count?${countParams}`, {
        headers
      });

      if (countResponse.ok) {
        const countData = await countResponse.json();
        setTotalOrders(countData.count);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    // If changing to shipped status, show tracking modal
    if (newStatus === 'shipped') {
      setTrackingOrderId(orderId);
      setShowTrackingModal(true);
      return;
    }

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${orderId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update order status');
      }

      fetchOrders();
    } catch (err) {
      setError(err.message);
    }
  };

  const updateOrderWithTracking = async () => {
    if (!trackingNumber.trim()) {
      setError("Please enter a tracking number");
      return;
    }

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${trackingOrderId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ 
          status: 'shipped',
          tracking_number: trackingNumber,
          shipping_carrier: trackingCarrier
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update order with tracking information');
      }

      setSuccessMessage('Order updated and shipping notification email sent!');
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Reset modal
      setShowTrackingModal(false);
      setTrackingNumber("");
      setTrackingCarrier("Australia Post");
      setTrackingOrderId(null);
      
      fetchOrders();
    } catch (err) {
      setError(err.message);
    }
  };

  const viewOrderDetail = async (orderId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${orderId}`, {
        headers
      });

      if (response.ok) {
        const orderData = await response.json();
        setOrderDetail(orderData);
        setShowOrderDetail(true);
      } else {
        throw new Error('Failed to fetch order details');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${orderId}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to delete order');
      }

      // Refresh orders
      fetchOrders();
      setSuccessMessage('Order deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [currentPage, statusFilter, searchTerm]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    // Convert UTC time to Adelaide/South Australia timezone
    return new Date(dateString).toLocaleString('en-AU', {
      timeZone: 'Australia/Adelaide',
      day: 'numeric',
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getCustomerName = (order) => {
    if (order.customer && order.customer.first_name) {
      return `${order.customer.first_name} ${order.customer.last_name || ''}`.trim();
    }
    return order.customer_name || 'Customer';
  };

  const getCustomerEmail = (order) => {
    if (order.customer && order.customer.email) {
      return order.customer.email;
    }
    return order.customer_email || '';
  };

  const getOrderTotal = (order) => {
    // Handle both total and total_amount fields
    const total = order.total || order.total_amount || 0;
    return isNaN(total) ? 0 : total;
  };

  const getStatusBadge = (status) => {
    const statusInfo = orderStatuses.find(s => s.value === status);
    return (
      <Badge className={statusInfo?.color || "bg-gray-100 text-gray-800"}>
        {statusInfo?.label || status}
      </Badge>
    );
  };

  const totalPages = Math.ceil(totalOrders / ordersPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {isManager ? (
                <Button variant="ghost" size="sm" onClick={handleManagerLogout} className="flex items-center gap-2">
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              ) : (
                <Button asChild variant="ghost" size="sm">
                  <div onClick={() => navigate('/admin/dashboard')} className="cursor-pointer flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Dashboard
                  </div>
                </Button>
              )}
              <div className="flex items-center gap-3">
                <div className="bg-blue-600 w-10 h-10 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Manage Orders</h1>
                  <p className="text-sm text-gray-500">{totalOrders} total orders</p>
                </div>
              </div>
            </div>
            
            <Button onClick={fetchOrders} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-600">{error}</AlertDescription>
          </Alert>
        )}

        {successMessage && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-600">{successMessage}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search orders by number, customer, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {orderStatuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        <Card>
          <CardHeader>
            <CardTitle>Orders</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading orders...</span>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
                <p className="text-gray-500">
                  {searchTerm || statusFilter ? "Try adjusting your filters" : "Orders will appear here once customers make purchases"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => (
                      <tr key={order._id || order.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="bg-blue-100 p-2 rounded">
                              <Package className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                #{order.order_number}
                              </div>
                              <div className="text-sm text-gray-500">
                                {order.items?.length || 0} items
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {getCustomerName(order)}
                              </div>
                              <div className="text-sm text-gray-500">
                                {getCustomerEmail(order)}
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Select 
                            value={order.status} 
                            onValueChange={(newStatus) => updateOrderStatus(order._id || order.id, newStatus)}
                          >
                            <SelectTrigger className="w-48">
                              <SelectValue>
                                {getStatusBadge(order.status)}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {orderStatuses.map((status) => (
                                <SelectItem key={status.value} value={status.value}>
                                  {status.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <CreditCard className="w-4 h-4 text-gray-400 mr-2" />
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(getOrderTotal(order))}
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                            <div className="text-sm text-gray-900">
                              {formatDate(order.created_at)}
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => viewOrderDetail(order._id || order.id)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            {order.status === 'shipped' && order.tracking_number && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => window.open(`https://auspost.com.au/mypost/track/#/details/${order.tracking_number}`, '_blank')}
                              >
                                <Truck className="w-4 h-4 mr-1" />
                                Track
                              </Button>
                            )}
                            {!isManager && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => deleteOrder(order._id || order.id)}
                                className="text-red-600 border-red-300 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing {((currentPage - 1) * ordersPerPage) + 1} to {Math.min(currentPage * ordersPerPage, totalOrders)} of {totalOrders} orders
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages, currentPage - 2 + i));
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === currentPage ? "default" : "outline"}
                        onClick={() => setCurrentPage(pageNum)}
                        className={pageNum === currentPage ? "bg-blue-600" : ""}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tracking Number Modal */}
        {showTrackingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-green-600" />
                    Add Tracking Information
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setShowTrackingModal(false);
                      setTrackingNumber("");
                      setTrackingCarrier("Australia Post");
                      setTrackingOrderId(null);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <Alert className="border-blue-200 bg-blue-50">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    A shipping notification email will be sent to the customer automatically.
                  </AlertDescription>
                </Alert>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shipping Carrier
                  </label>
                  <Select value={trackingCarrier} onValueChange={setTrackingCarrier}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Australia Post">Australia Post</SelectItem>
                      <SelectItem value="Toll">Toll</SelectItem>
                      <SelectItem value="StarTrack">StarTrack</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tracking Number *
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter tracking number"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    onClick={updateOrderWithTracking}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={!trackingNumber.trim()}
                  >
                    <Truck className="w-4 h-4 mr-2" />
                    Mark as Shipped & Send Email
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowTrackingModal(false);
                      setTrackingNumber("");
                      setTrackingCarrier("Australia Post");
                      setTrackingOrderId(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Order Detail Modal */}
        {showOrderDetail && orderDetail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-semibold text-gray-900">
                    Order Details - #{orderDetail.order_number}
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowOrderDetail(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Customer Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-3">Customer Information</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Name:</strong> {getCustomerName(orderDetail)}</p>
                      <p><strong>Email:</strong> {getCustomerEmail(orderDetail)}</p>
                      <p><strong>Phone:</strong> {orderDetail.customer?.phone || 'Not provided'}</p>
                    </div>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-3">Order Information</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Status:</strong> {getStatusBadge(orderDetail.status)}</p>
                      <p><strong>Total:</strong> {formatCurrency(getOrderTotal(orderDetail))}</p>
                      <p><strong>Date:</strong> {formatDate(orderDetail.created_at)}</p>
                      <p><strong>Payment:</strong> {orderDetail.payment_method || 'Credit Card'}</p>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-4">Order Items</h4>
                  <div className="space-y-3">
                    {orderDetail.items?.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium">{item.product?.name || 'Product'}</p>
                          <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                          {item.product?.sku && (
                            <p className="text-xs text-gray-500">SKU: {item.product.sku}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">${(item.quantity * item.price).toFixed(2)}</p>
                          <p className="text-sm text-gray-600">${item.price.toFixed(2)} each</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Shipping Address */}
                {orderDetail.shipping_address && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-3">Shipping Address</h4>
                    <div className="text-sm">
                      <p>{orderDetail.shipping_address.first_name} {orderDetail.shipping_address.last_name}</p>
                      <p>{orderDetail.shipping_address.address_line_1}</p>
                      {orderDetail.shipping_address.address_line_2 && (
                        <p>{orderDetail.shipping_address.address_line_2}</p>
                      )}
                      <p>{orderDetail.shipping_address.city}, {orderDetail.shipping_address.state} {orderDetail.shipping_address.postal_code}</p>
                      <p>{orderDetail.shipping_address.country}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrdersPage;